import { IconBrandsFacebook } from '@doltech/icons/IconBrandsFacebook';
import { IconContentLink } from '@doltech/icons/IconContentLink';
import { IconContentList } from '@doltech/icons/IconContentList';
import { getLandingUrlFromPath } from '@doltech/utils/lib/url';
import cl from 'classnames';
import * as React from 'react';
import styled from 'styled-components';
import { useClipboard } from 'use-clipboard-copy';

import { deviceBreakpoints } from '../../../../hooks/useDeviceQuery.hook';
import { colorsV2 } from '../../../colors-v2';
import { Box } from '../../../Common/BoxV2';
import { DolFacebookShareButton } from '../../../Social/DolFacebookShareButton';
import { TOCDrawerProps } from '../Content/TableOfContent/TOCDrawer/TOCDrawer';
import { DolTooltip } from '../../../Tooltip/DolTooltip';
import dynamic from 'next/dynamic';

const TOCDrawer = dynamic<TOCDrawerProps>(() =>
  import('../Content/TableOfContent/TOCDrawer/TOCDrawer').then((mod) => mod.TOCDrawer)
);

const LAYOUT = {
  FILL: 'fill',
  RESPONSIVE: 'responsive',
} as const;

const Action = styled.div`
  padding: 4px;
  color: ${colorsV2.gray100};
  border-radius: 4px;

  &:not(:last-child) {
    margin-right: 24px;
  }

  &.active {
    background-color: ${colorsV2.black60};
    color: ${colorsV2.white100};
  }
`;

const ActionGroup = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  width: 100%;

  &:not(:last-child) {
    border-left: 1px solid ${colorsV2.gray20};
  }
`;

const Layout = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: flex-start;
`;

const Container = styled.aside`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  min-height: 52px;
  background-color: ${colorsV2.white100};
  border-top: 1px solid ${colorsV2.gray20};
  z-index: 10;
  @media ${deviceBreakpoints.fromDesktop} {
    display: none;
  }
`;

export interface InteractionBottomNavigationProps
  extends Pick<TOCDrawerProps, 'scrollContainerElm'> {
  layout?: typeof LAYOUT[keyof typeof LAYOUT];
  pageId?: string;
  pageUrl?: string;
  tooltipColor?: string;
  tocTitle?: string;
  tocItems?: TOCDrawerProps['items'];
  tocEditorItems?: TOCDrawerProps['editorItems'];
}

export const InteractionBottomNavigation = (props: InteractionBottomNavigationProps) => {
  const {
    layout = LAYOUT.FILL,
    pageId,
    pageUrl,
    tooltipColor = colorsV2.black100,
    tocTitle,
    tocItems,
    tocEditorItems,
    scrollContainerElm,
  } = props;

  const isFill = layout === LAYOUT.FILL;
  const [shouldInitTOC, initTOC] = React.useState(false);
  const [tocVisible, setTocVisible] = React.useState(false);
  const { copy, copied } = useClipboard({
    copiedTimeout: 1500, // timeout duration in milliseconds
  });

  const isValidTocItems = tocEditorItems?.length > 0 || tocItems?.length > 0;

  return (
    <Container>
      <Layout>
        {isValidTocItems && (
          <ActionGroup maxWidth="96px">
            <Action
              onClick={() => {
                setTocVisible(true);
                initTOC(true);
              }}
            >
              <IconContentList fontSize={28} color="gray100" />
              {shouldInitTOC && (
                <TOCDrawer
                  visible={tocVisible}
                  onClose={(e) => {
                    if (e) { // because the TOCDrawer onClose does not always have event - some call from div elements
                      e.stopPropagation();
                    }
                    setTocVisible(false);
                  }}
                  tocTitle={tocTitle}
                  items={tocItems}
                  editorItems={tocEditorItems}
                  scrollContainerElm={scrollContainerElm}
                />
              )}
            </Action>
          </ActionGroup>
        )}
        <ActionGroup maxWidth={isFill ? '100%' : ['100%', '140px']}>
          <Action>
            <DolFacebookShareButton
              pageId={pageId}
              className="social-button"
              key="facebook-share"
              url={pageUrl}
            >
              <IconBrandsFacebook fontSize={28} />
            </DolFacebookShareButton>
          </Action>
          <DolTooltip
            trigger={['click']}
            title="Đã sao chép link"
            placement="top"
            visible={copied}
            color={tooltipColor}
          >
            <Action
              className={cl({ active: copied })}
              onClick={() => copy(getLandingUrlFromPath(pageUrl))}
            >
              <IconContentLink fontSize={28} />
            </Action>
          </DolTooltip>
        </ActionGroup>
        <ActionGroup maxWidth={isFill ? '100%' : ['100%', '140px']}>
          <Action></Action>
        </ActionGroup>
      </Layout>
    </Container>
  );
};
